import { z } from 'zod';
import { uuidSchema } from './common.schema.js';

/**
 * Patient Assignment Schemas - SSOT for patient_assignments model
 *
 * patient_assignments replaces the old patient_staff_links table.
 * A NULL effective_to means the assignment is currently active.
 */

export const BasePatientAssignmentSchema = z.object({
  assignmentId: uuidSchema,
  patientId: uuidSchema,
  encounterId: uuidSchema,
  staffId: z.string().min(1).max(200),
  hospitalId: uuidSchema,
  assignmentRole: z.string().min(1).max(30),
  effectiveFrom: z.coerce.date(),
  effectiveTo: z.coerce.date().nullable(),
  notes: z.string().max(500).nullable().optional(),
  createdBy: z.string().max(200).nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type BasePatientAssignment = z.infer<typeof BasePatientAssignmentSchema>;

// ---------------------------------------------------------------------------
// POST /  — staffId is in the request body along with patients array
// ---------------------------------------------------------------------------
export const AssignmentRoleSchema = z.enum([
  'PRIMARY_NURSE',
  'SECONDARY_NURSE',
  'DIETITIAN',
  'ATTENDING_PHYSICIAN',
  'TECHNICIAN',
  'OTHER',
]);
export type AssignmentRole = z.infer<typeof AssignmentRoleSchema>;

export const PatientAssignmentItemSchema = z.object({
  patientId: uuidSchema,
  encounterId: uuidSchema,
  hospitalId: uuidSchema,
});
export type PatientAssignmentItem = z.infer<typeof PatientAssignmentItemSchema>;

export const AddPatientAssignmentsRequestSchema = z.object({
  staffId: z.string().min(1).max(200),
  patients: z.array(PatientAssignmentItemSchema).nonempty('At least one patient entry is required'),
});
export type AddPatientAssignmentsRequest = z.infer<typeof AddPatientAssignmentsRequestSchema>;

// ---------------------------------------------------------------------------
// PATCH /:staffId  — staffId comes from URL param, body is an array of patientIds
// ---------------------------------------------------------------------------
export const EndPatientAssignmentsRequestSchema = z.object({
  patientIds: z.array(uuidSchema).nonempty('At least one patient ID is required'),
});
export type EndPatientAssignmentsRequest = z.infer<typeof EndPatientAssignmentsRequestSchema>;

// ---------------------------------------------------------------------------
// GET /patient/:patientId/staff  — returns list of staff with user details
// ---------------------------------------------------------------------------
export const StaffUserInfoSchema = z.object({
  userId: uuidSchema,
  username: z.string(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  jobTitle: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  employeeNumber: z.string().nullable().optional(),
});
export type StaffUserInfo = z.infer<typeof StaffUserInfoSchema>;

export const PatientAssignmentResponseSchema = BasePatientAssignmentSchema;
export type PatientAssignmentResponse = z.infer<typeof PatientAssignmentResponseSchema>;
